import { motion } from "framer-motion";
import { FileText, Download, FileBadge, FlaskConical } from "lucide-react";
import { Reveal, MaskLineInView } from "./Reveal";

const FILES = [
  {
    testid: "download-profile",
    href: "/polycaps-company-profile.pdf",
    file: "Polycaps-Company-Profile.pdf",
    icon: FileText,
    label: "Company Profile",
    desc: "Our vision, mission, capabilities and the full Polycaps story.",
    meta: "PDF · 2.4 MB",
  },
  {
    testid: "download-datasheet",
    href: "/alaska-27mm-datasheet.pdf",
    file: "ALASKA-27MM-Datasheet.pdf",
    icon: FileBadge,
    label: "ALASKA 27MM Datasheet",
    desc: "Full specifications, features and packaging for our flagship cap.",
    meta: "PDF · 1 page",
  },
  {
    testid: "download-labreport",
    href: "/polycaps-lab-report.pdf",
    file: "Polycaps-Sample-Lab-Report.pdf",
    icon: FlaskConical,
    label: "Sample Lab Report",
    desc: "NABL / ISO 17025 test report — IS 15410:2025 compliance for the ALASKA cap.",
    meta: "PDF · Test report",
  },
];

export const Downloads = () => (
  <section id="downloads" data-testid="downloads-section" className="bg-white py-16 md:py-36">
    <div className="max-w-[1400px] mx-auto px-5 md:px-10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <Reveal>
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-[var(--brand)]">
              Downloads
            </span>
          </Reveal>
          <h2 className="font-display font-semibold tracking-tighter leading-[1.02] sm:leading-[0.98] mt-5 text-3xl sm:text-4xl md:text-5xl">
            <MaskLineInView>Everything you</MaskLineInView>
            <MaskLineInView delay={0.08}>need, one tap away.</MaskLineInView>
          </h2>
          <Reveal delay={0.15}>
            <p className="mt-6 text-lg leading-relaxed text-[var(--muted)]">
              Grab our company profile and the ALASKA 27MM datasheet — ready to share with your
              procurement and quality teams.
            </p>
          </Reveal>
        </div>

        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {FILES.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.a
                key={f.testid}
                data-testid={f.testid}
                href={f.href}
                download={f.file}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: i * 0.1 }}
                className="group relative flex flex-col justify-between rounded-3xl border border-slate-200 bg-[var(--surface)] p-8 hover:bg-[var(--ink)] hover:-translate-y-1 transition-[transform,background-color] duration-300"
              >
                <div>
                  <div className="w-12 h-12 rounded-2xl bg-[var(--brand)]/10 group-hover:bg-white/15 flex items-center justify-center text-[var(--brand)] group-hover:text-white transition-colors duration-300">
                    <Icon size={22} />
                  </div>
                  <h3 className="font-display text-xl font-medium mt-6 tracking-tight text-[var(--ink)] group-hover:text-white transition-colors duration-300">
                    {f.label}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-[var(--muted)] group-hover:text-slate-300 transition-colors duration-300">
                    {f.desc}
                  </p>
                </div>
                <div className="mt-8 flex items-center justify-between">
                  <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--muted)] group-hover:text-slate-400 transition-colors duration-300">
                    {f.meta}
                  </span>
                  <span className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--brand)] group-hover:text-white transition-colors duration-300">
                    Download
                    <Download size={16} className="group-hover:translate-y-0.5 transition-transform" />
                  </span>
                </div>
              </motion.a>
            );
          })}
        </div>
      </div>
    </div>
  </section>
);
